// src/services/wordInfoService.js
// Word lookups shared by the crossword editor and Terälehti: Asiasanasto definitions,
// Ratkojat clue suggestions, clues used in earlier Norppa puzzles, and cached icon URLs.
import { getIconUrl } from './iconService';

const PREVIEW_URL = 'https://asiasanasto.fi/api/word-preview/';
const SUGGEST_URL = 'https://suggestclues-jfyvqibepa-uc.a.run.app?word=';

const cached = (cache, key, load) => {
  if (!cache.has(key)) {
    cache.set(key, load().catch(e => {
      cache.delete(key);
      return { error: e.message };
    }));
  }
  return cache.get(key);
};

const previewCache = new Map();
const ratkojatCache = new Map();
const iconUrlCache = new Map();

// Resolves to the preview, null when Asiasanasto has no entry, or { error } when the request fails.
// Needs CORS enabled on Asiasanasto's /api/word-preview route.
export const fetchPreview = (word) => cached(previewCache, word.toLowerCase(), () =>
  fetch(PREVIEW_URL + encodeURIComponent(word.toLowerCase()))
    .then(r => (r.status === 404 ? null : r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))));

// Resolves to an array of Ratkojat clues, or { error }.
export const fetchRatkojatClues = (word) => cached(ratkojatCache, word.toLowerCase(), () =>
  fetch(SUGGEST_URL + encodeURIComponent(word.toLowerCase()))
    .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then(d => d.clues || []));

// Resolves to the download URL or null.
export const fetchIconUrl = (name) => cached(iconUrlCache, name, () => getIconUrl(name))
  .then(u => (typeof u === 'string' ? u : null));

const dateKey = (id) => id.split('-').reverse().join('-'); // dd-mm-yyyy -> yyyy-mm-dd

// ANSWER -> [{ clue, date }] across every puzzle except `excludeId`, newest first.
// Clues keep their real soft hyphens; callers convert for display or editing.
export const buildPastClueIndex = (puzzles, excludeId) => {
  const index = new Map();
  for (const p of puzzles || []) {
    if (p.id === excludeId) continue;
    for (const w of p.words || []) {
      const clue = (w.clue || '').trim();
      if (!clue || !w.answer) continue;
      const answer = w.answer.toUpperCase();
      if (!index.has(answer)) index.set(answer, []);
      index.get(answer).push({ clue, date: p.id });
    }
  }
  for (const list of index.values()) list.sort((a, b) => dateKey(b.date).localeCompare(dateKey(a.date)));
  return index;
};
